import { normalizeNumber } from 'utils/formats/normalizeNumber';

export const mergeOrderBook = (oldOrders: string[][], newOrders: string[][]) => {
  const orderMap = new Map();

  oldOrders.forEach(([price, amount]) => {
    orderMap.set(price, amount);
  });

  newOrders.forEach(([price, amount]) => {
    if (amount === '0') {
      orderMap.delete(price);
    } else {
      orderMap.set(price, amount);
    }
  });

  return [...orderMap.entries()] as [string, string][];
};

export const sortOrderbookFunc = (a: [string, string], b: [string, string]) => {
  const aNum = Number(a[0]);
  const bNum = Number(b[0]);

  if (aNum === bNum) return 0;
  if (aNum > bNum) return 1;
  return -1;
};

// Keep the nearest `depth` levels: lowest asks, highest bids.
// `sortOrderbookFunc` is ascending, so `slice(0, depth)` on bids would keep the
// farthest bids and punch a hole under last (pair 691 / Biconomy).
export const keepNearestOrderbookLevels = (
  asks: [string, string][],
  bids: [string, string][],
  depth: number,
): { asks: [string, string][]; bids: [string, string][] } => {
  const safeDepth = Math.max(0, depth);
  const sortedAsks = [...asks].sort(sortOrderbookFunc);
  const sortedBids = [...bids].sort(sortOrderbookFunc);

  return {
    asks: sortedAsks.slice(0, safeDepth),
    bids: safeDepth === 0 ? [] : sortedBids.slice(-safeDepth),
  };
};

export const aggregateOrderbook = (
  asks: string[][],
  bids: string[][],
  precision: number,
  depth = 50,
) => {
  const roundPrice = (price: number, precision: number, roundFn: (n: number) => number) => {
    const factor = 10 ** precision;
    return (roundFn(price * factor) / factor).toFixed(precision);
  };

  const newAsks = asks.reduce(
    (acc, val) => {
      const price = roundPrice(Number(val[0]), precision, Math.ceil);

      acc[price] = (acc[price] || 0) + Number(val[1]);

      return acc;
    },
    {} as Record<string, number>,
  );

  const newBids = bids.reduce(
    (acc, val) => {
      const price = roundPrice(Number(val[0]), precision, Math.floor);

      acc[price] = (acc[price] || 0) + Number(val[1]);

      return acc;
    },
    {} as Record<string, number>,
  );

  const newAsksFlat = Object.keys(newAsks)
    .map(key => [key, normalizeNumber(newAsks[key])])
    .filter(el => Number(el[0]) !== 0 && Number(el[1]) !== 0)
    .sort((a, b) => Number(a[0]) - Number(b[0]));

  const newBidsFlat = Object.keys(newBids)
    .map(key => [key, normalizeNumber(newBids[key])])
    .filter(el => Number(el[0]) !== 0 && Number(el[1]) !== 0)
    .sort((a, b) => Number(b[0]) - Number(a[0]));

  return {
    asks: newAsksFlat.slice(0, depth),
    bids: newBidsFlat.slice(0, depth),
  };
};
